import { useMemo } from 'react'

import { useF0FormDefinition, f0FormField } from '@factorialco/f0-react'
import { UpsertAiCreditsCreditSettingDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

const useCreditsSchema = () => {
  return useMemo(
    () =>
      z.object({
        employeeCreditCap: f0FormField(z.number().int().positive().optional(), {
          label: i18n.t('ai.settings.credits_tab.form.monthly_employee_allowance_label'),
          helpText: i18n.t('ai.settings.credits_tab.form.monthly_employee_allowance_helper'),
          fieldType: 'money' as const,
          currency: 'credits',
          alert: {
            title: i18n.t('ai.settings.credits_tab.form.monthly_employee_allowance_alert'),
            description: '',
            variant: 'info' as const,
          },
        }),
      }),
    []
  )
}

interface UseCreditsFormParams {
  employeeCreditCap: number | null | undefined
}

export const useCreditsForm = ({ employeeCreditCap }: UseCreditsFormParams) => {
  const schema = useCreditsSchema()

  const [upsertCreditSetting] = useMutation(UpsertAiCreditsCreditSettingDocument, {
    extractorFn: (data) => data.aiCreditsMutations.upsertAicreditsCreditSetting,
  })

  const formDefinition = useF0FormDefinition({
    name: 'ai-credits-per-employee',
    schema,
    defaultValues: {
      employeeCreditCap: employeeCreditCap ?? undefined,
    },
    submitConfig: {
      type: 'action-bar',
      label: i18n.t('common.save'),
    },
    onSubmit: async ({ data }) => {
      let errors: Record<string, string> = {}

      const result = await upsertCreditSetting({
        variables: {
          employeeCreditCap: data.employeeCreditCap ?? null,
        },
      })

      const handled = handleResult(result, (formErrors) => {
        errors = formErrors
      })

      if (handled.success) {
        return { success: true }
      }

      return { success: false, errors }
    },
  })

  return { formDefinition }
}
